/**
 * <predictive-search>
 * ---------------------------------------------------------------------------
 * Buscador con desplegable: según se escribe, muestra sugerencias de búsqueda y
 * fichas de producto.
 *
 * El campo se comporta distinto según el ancho, y el CSS manda:
 *   - Escritorio: siempre visible en su columna de la cabecera. La lupa no se
 *     pinta y aquí `this.toggle` puede no existir.
 *   - Móvil: la lupa despliega el campo en una franja bajo la barra.
 *
 * Por eso lo único obligatorio son el campo y el contenedor de resultados: el
 * botón y el panel son opcionales.
 *
 * El HTML de los resultados lo genera el servidor
 * (`sections/predictive-search.liquid`) y aquí solo se pega. Formatear precios
 * en JavaScript significa reimplementar monedas, decimales e impuestos, y que
 * el desplegable mienta el día que la tienda cambie de país.
 *
 * Sin este archivo el formulario sigue enviándose a /search: el desplegable es
 * una comodidad, no el único camino.
 *
 * Envuelto en IIFE: la cabecera se recarga sola en el editor de temas y dos
 * <script> con la misma URL se ejecutan dos veces. Sin el envoltorio, el
 * `class` del ámbito global choca y el archivo entero deja de funcionar.
 */

(() => {
  const DEBOUNCE_MS = 250;
  const MIN_LENGTH = 2;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-search-toggle]');
      this.panel = this.querySelector('[data-search-panel]');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;

      this.toggle?.addEventListener('click', () => this.#toggleOpen());
      this.input.addEventListener('input', () => this.#onInput());
      this.addEventListener('keydown', this.#onKeydown);

      // Pulsar fuera cierra. Va en `document` porque el clic que interesa
      // ocurre, por definición, fuera de este elemento.
      document.addEventListener('click', this.#onDocumentClick);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      document.removeEventListener('click', this.#onDocumentClick);
    }

    /**
     * "Abierto" es que se vean las sugerencias, no que se vea el campo: en
     * escritorio el campo está siempre ahí y el panel no se oculta nunca.
     */
    get isOpen() {
      return !this.results.hidden;
    }

    /** ¿Está la lupa a la vista? Solo entonces el panel se abre y se cierra. */
    get #hasToggle() {
      return this.toggle !== null && this.toggle.offsetParent !== null;
    }

    open() {
      if (this.panel) this.panel.hidden = false;
      this.toggle?.setAttribute('aria-expanded', 'true');
      this.input.focus();
    }

    close() {
      // El campo solo se repliega donde hay lupa que lo vuelva a sacar.
      if (this.#hasToggle && this.panel) this.panel.hidden = true;
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.#render('');
    }

    #toggleOpen() {
      if (this.panel && !this.panel.hidden) {
        this.close();
        this.toggle?.focus();
      } else {
        this.open();
      }
    }

    #onInput() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();

      if (term.length < MIN_LENGTH) {
        this.#render('');
        return;
      }

      this.timer = setTimeout(() => this.#search(term), DEBOUNCE_MS);
    }

    async #search(term) {
      // Cada pulsación cancela la petición anterior. Sin esto, una respuesta
      // lenta de hace tres letras puede llegar la última y pisar la buena.
      this.controller?.abort();
      this.controller = new AbortController();

      const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}` +
        `&resources[type]=product,query&resources[limit]=6` +
        `&section_id=${this.dataset.sectionId}`;

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.statusText);
        this.#render(await response.text());
      } catch (error) {
        // Abortar es lo normal aquí, no un fallo: solo importa lo demás.
        if (error.name === 'AbortError') return;
        this.#render('');
      }
    }

    #render(html) {
      this.results.innerHTML = html;
      const hasResults = this.results.querySelector('[data-predictive-link]') !== null;
      this.results.hidden = html === '';
      this.input.setAttribute('aria-expanded', String(hasResults));
      this.#clearHighlight();
    }

    get #links() {
      return Array.from(this.results.querySelectorAll('[data-predictive-link]'));
    }

    #clearHighlight() {
      this.index = -1;
      this.#links.forEach((link) => link.classList.remove('is-highlighted'));
      this.input.removeAttribute('aria-activedescendant');
    }

    /**
     * Flechas para recorrer los resultados. El foco NO se mueve: se queda en el
     * campo de texto y solo se marca la opción actual, que es lo que espera un
     * combobox. Mover el foco de verdad obligaría a devolverlo al escribir la
     * siguiente letra, y el cursor saltaría.
     */
    #move(step) {
      const links = this.#links;
      if (links.length === 0) return;

      links.forEach((link) => link.classList.remove('is-highlighted'));
      this.index = (this.index + step + links.length) % links.length;

      const current = links[this.index];
      current.classList.add('is-highlighted');
      current.scrollIntoView({ block: 'nearest' });

      if (!current.id) current.id = `predictive-option-${this.index}`;
      this.input.setAttribute('aria-activedescendant', current.id);
    }

    #onKeydown = (event) => {
      // Escape tiene que funcionar aunque no haya resultados a la vista: en
      // móvil es lo que repliega el campo.
      if (!this.isOpen && event.key !== 'Escape') return;

      if (event.key === 'Escape') {
        this.close();
        if (this.#hasToggle) this.toggle.focus();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.#move(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      // Con una opción marcada, Enter va a esa opción. Sin ninguna, el
      // formulario se envía solo y lleva a la página de resultados.
      if (event.key === 'Enter' && this.index > -1) {
        const current = this.#links[this.index];
        if (current) {
          event.preventDefault();
          current.click();
        }
      }
    };

    #onDocumentClick = (event) => {
      if (this.isOpen && !this.contains(event.target)) this.close();
    };
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
